import { Container } from "pixi.js";
import { Card } from "./Card";
import { Deck } from "./Deck";
import { Pile } from "./Pile";
import { BackgroundPattern } from "./BackgroundPattern";
import { MatrixBackground } from "./MatrixBackground";

export interface BoardLayout {
  tableauStartX: number;
  tableauStartY: number;
  tableauSpacing: number;
  cardSpacing: number;
  foundationStartX: number;
  foundationStartY: number;
  stockX: number;
  stockY: number;
  wasteX: number;
  wasteY: number;
  width?: number;
  height?: number;
}

/**
 * Calculate responsive layout based on screen dimensions
 */
function calculateResponsiveLayout(screenWidth: number, screenHeight: number): BoardLayout {
  const cardWidth = 80;
  const cardHeight = 120;
  const isPortrait = screenHeight > screenWidth;
  const isMobile = screenWidth < 768;
  
  // Base spacing that scales with screen size
  const baseSpacing = Math.min(screenWidth / 12, 100);
  const cardSpacing = Math.min(screenHeight / 24, 30);
  
  if (isPortrait && isMobile) {
    // Mobile portrait: compact vertical layout
    const centerX = screenWidth / 2;
    const margin = Math.max(10, screenWidth / 40);
    
    // Stock and waste at top center, close together
    const stockWasteSpacing = Math.min(margin * 0.3, 12); // Closer spacing on mobile too
    const stockWasteWidth = cardWidth * 2 + stockWasteSpacing;
    const stockX = centerX - stockWasteWidth / 2;
    const wasteX = stockX + cardWidth + stockWasteSpacing;
    const topY = margin + 20; // Space for labels
    
    // Foundation piles below stock/waste, centered
    const foundationY = topY + cardHeight + margin + 30;
    const foundationAreaWidth = cardWidth * 4 + Math.min(margin, 10) * 3;
    const foundationStartX = centerX - foundationAreaWidth / 2;
    const foundationSpacing = Math.min(margin, 10);
    
    // Tableau starts below foundation, centered
    const tableauStartY = foundationY + cardHeight + margin + 30;
    const tableauAreaWidth = cardWidth * 7 + Math.min(margin, 8) * 6;
    const tableauStartX = centerX - tableauAreaWidth / 2;
    const tableauSpacing = Math.min(margin, 8);
    
    return {
      stockX,
      stockY: topY,
      wasteX,
      wasteY: topY,
      foundationStartX,
      foundationStartY: foundationY,
      tableauStartX,
      tableauStartY,
      tableauSpacing,
      cardSpacing: Math.min(cardSpacing, 25), // Tighter spacing on mobile
    };
  } else {
    // Landscape/wide screen: traditional layout, centered
    const centerX = screenWidth / 2;
    const margin = Math.max(20, screenWidth / 30);
    const topY = margin + 20;
    
    // Calculate spacing between elements
    // Stock and waste should be close together (smaller spacing)
    const stockWasteSpacing = Math.min(baseSpacing * 0.4, 30); // Closer spacing between stock and waste
    const wasteFoundationGap = baseSpacing * 2.5; // More space between waste and foundation
    const foundationAreaWidth = cardWidth * 4 + baseSpacing * 3;
    
    // Calculate total width of top row: stock + spacing + waste + gap + foundation area
    const topRowWidth = cardWidth + stockWasteSpacing + cardWidth + wasteFoundationGap + foundationAreaWidth;
    
    // Center the entire top row
    const topRowStartX = centerX - topRowWidth / 2;
    
    // Position stock, waste, and foundation relative to centered top row
    const stockX = topRowStartX;
    const wasteX = stockX + cardWidth + stockWasteSpacing;
    const foundationStartX = wasteX + cardWidth + wasteFoundationGap;
    
    // Tableau: 7 piles centered below
    const tableauAreaWidth = cardWidth * 7 + baseSpacing * 6;
    const tableauStartX = centerX - tableauAreaWidth / 2;
    const tableauStartY = topY + cardHeight + margin + 40;
    
    return {
      stockX,
      stockY: topY,
      wasteX,
      wasteY: topY,
      foundationStartX,
      foundationStartY: topY,
      tableauStartX,
      tableauStartY,
      tableauSpacing: baseSpacing,
      cardSpacing,
    };
  }
}

const DEFAULT_LAYOUT: BoardLayout = {
  tableauStartX: 50,
  tableauStartY: 200,
  tableauSpacing: 100,
  cardSpacing: 30,
  foundationStartX: 600,
  foundationStartY: 50,
  stockX: 50,
  stockY: 50,
  wasteX: 150,
  wasteY: 50,
};

export class GameBoard extends Container {
  public readonly tableau: Pile[] = [];
  public readonly foundation: Pile[] = [];
  public readonly stock: Pile;
  public readonly waste: Pile;
  
  private deck: Deck;
  private layout: BoardLayout;
  private feltPattern: ReturnType<typeof BackgroundPattern.createFeltPattern> | null = null;
  private matrixBackground: MatrixBackground | null = null;
  private currentBackgroundColor: string = 'green';
  private currentBackgroundType: 'felt' | 'matrix' = 'matrix';

  constructor(layout: Partial<BoardLayout> = {}) {
    super();
    this.name = "GameBoard";
    
    // Calculate responsive layout if dimensions are provided
    if (layout.width && layout.height) {
      const responsiveLayout = calculateResponsiveLayout(layout.width, layout.height);
      this.layout = { ...responsiveLayout, ...layout };
    } else {
      this.layout = { ...DEFAULT_LAYOUT, ...layout };
    }
    
    this.deck = new Deck();

    // Create background pattern (low coupling - uses BackgroundPattern utility)
    this.createBackground();

    this.stock = this.createStockPile();
    this.waste = this.createWastePile();
    this.createTableauPiles();
    this.createFoundationPiles();

    this.addChild(this.stock);
    this.addChild(this.waste);
    this.tableau.forEach(pile => this.addChild(pile));
    this.foundation.forEach(pile => this.addChild(pile));
  }

  private createBackground(): void {
    // Background should fill entire canvas area
    // Use a very large size to ensure it covers the entire canvas
    // even when stage is scaled and centered with letterboxing
    // Size it to cover maximum possible canvas area in stage coordinates
    const viewportWidth = window.innerWidth || 1920;
    const viewportHeight = window.innerHeight || 1080;
    const designWidth = this.layout.width ?? 1280;
    const designHeight = this.layout.height ?? 720;
    
    // Calculate what the canvas size would be in stage coordinates
    // When stage is scaled and centered, we need to cover the entire canvas area
    // Use the larger dimension to ensure full coverage
    const maxDimension = Math.max(viewportWidth, viewportHeight);
    
    // Size background to cover entire canvas area in stage coordinates
    // Account for stage scaling - use a size that covers the entire viewport
    // when converted to stage coordinates
    const scaleX = viewportWidth / designWidth;
    const scaleY = viewportHeight / designHeight;
    const minScale = Math.min(scaleX, scaleY); // Stage uses min scale to maintain aspect ratio
    
    // Calculate canvas size in stage coordinates
    const canvasWidthInStage = viewportWidth / minScale;
    const canvasHeightInStage = viewportHeight / minScale;
    
    // Add padding to ensure coverage even when stage is centered/scaled
    // Reduced from 2000 to 500 for more reasonable background size
    // 500px padding should be sufficient for most screen sizes and stage transforms
    const padding = 500;
    const width = Math.ceil(canvasWidthInStage) + padding * 2;
    const height = Math.ceil(canvasHeightInStage) + padding * 2;

    // Create background based on current type
    if (this.currentBackgroundType === 'matrix') {
      this.createMatrixBackground(width, height, padding);
    } else {
      this.createFeltBackground(width, height, padding);
    }
  }

  private createFeltBackground(width: number, height: number, padding: number): void {
    // Remove matrix background if exists
    if (this.matrixBackground) {
      this.removeChild(this.matrixBackground);
      this.matrixBackground.destroy();
      this.matrixBackground = null;
    }
    
    // Remove old felt pattern if exists
    if (this.feltPattern) {
      this.removeChild(this.feltPattern);
      this.feltPattern.destroy();
      this.feltPattern = null;
    }
    
    // Create felt pattern background
    this.feltPattern = BackgroundPattern.createFeltPattern(width, height, this.currentBackgroundColor);
    this.feltPattern.name = "FeltPattern";
    this.feltPattern.x = -padding;
    this.feltPattern.y = -padding;
    this.addChildAt(this.feltPattern, 0);
  }

  private createMatrixBackground(width: number, height: number, padding: number): void {
    // Remove felt pattern if exists
    if (this.feltPattern) {
      this.removeChild(this.feltPattern);
      this.feltPattern.destroy();
      this.feltPattern = null;
    }
    
    // Calculate visible size (without padding) for Matrix effect calculation
    const visibleWidth = width - padding * 2;
    const visibleHeight = height - padding * 2;
    
    // Create matrix background
    // Pass full width/height (with padding) for background graphics coverage
    // Pass visible width/height (without padding) for Matrix effect calculation
    this.matrixBackground = new MatrixBackground(width, height, visibleWidth, visibleHeight);
    this.matrixBackground.name = "MatrixBackground";
    // Start at (0, 0) - position will be set correctly by setupBackgroundResize() in SolitaireGame
    // This ensures it's visible initially and will be repositioned properly when stage transforms
    this.matrixBackground.x = 0;
    this.matrixBackground.y = 0;
    this.addChildAt(this.matrixBackground, 0);
  }

  /**
   * Update background pattern size to match current canvas dimensions
   */
  updateBackgroundSize(width: number, height: number): void {
    // Use same padding as used when creating backgrounds (500px)
    const padding = 500;
    
    if (this.currentBackgroundType === 'matrix') {
      if (this.matrixBackground) {
        // Calculate visible size (without padding) for Matrix effect calculation
        const visibleWidth = width - padding * 2;
        const visibleHeight = height - padding * 2;
        // Resize to full canvas size (with padding) for background graphics
        // Use visible size (without padding) for Matrix effect calculation
        // Position will be set by setupBackgroundResize() in SolitaireGame
        this.matrixBackground.resize(width, height, visibleWidth, visibleHeight);
      } else {
        this.createMatrixBackground(width, height, padding);
      }
    } else {
      // Use helper method to ensure proper cleanup
      this.createFeltBackground(width, height, padding);
    }
  }

  /**
   * Change background color scheme or type
   */
  changeBackground(backgroundType: string): void {
    // Check if it's a background type (matrix) or color scheme (felt colors)
    if (backgroundType === 'matrix') {
      this.currentBackgroundType = 'matrix';
      this.currentBackgroundColor = 'green'; // Keep color for when switching back
      
      // Calculate dimensions
      const viewportWidth = window.innerWidth || 1920;
      const viewportHeight = window.innerHeight || 1080;
      const designWidth = this.layout.width ?? 1280;
      const designHeight = this.layout.height ?? 720;
      
      const scaleX = viewportWidth / designWidth;
      const scaleY = viewportHeight / designHeight;
      const minScale = Math.min(scaleX, scaleY);
      
      const canvasWidthInStage = viewportWidth / minScale;
      const canvasHeightInStage = viewportHeight / minScale;
      
      const padding = 500;
      const width = Math.ceil(canvasWidthInStage) + padding * 2;
      const height = Math.ceil(canvasHeightInStage) + padding * 2;
      
      // Remove old background
      if (this.feltPattern) {
        this.removeChild(this.feltPattern);
        this.feltPattern.destroy();
        this.feltPattern = null;
      }
      
      // Create matrix background
      this.createMatrixBackground(width, height, padding);
    } else {
      // It's a color scheme for felt background
      this.currentBackgroundType = 'felt';
      this.currentBackgroundColor = backgroundType;
      
      // Calculate dimensions
      const viewportWidth = window.innerWidth || 1920;
      const viewportHeight = window.innerHeight || 1080;
      const designWidth = this.layout.width ?? 1280;
      const designHeight = this.layout.height ?? 720;
      
      const scaleX = viewportWidth / designWidth;
      const scaleY = viewportHeight / designHeight;
      const minScale = Math.min(scaleX, scaleY);
      
      const canvasWidthInStage = viewportWidth / minScale;
      const canvasHeightInStage = viewportHeight / minScale;
      
      const padding = 500;
      const width = Math.ceil(canvasWidthInStage) + padding * 2;
      const height = Math.ceil(canvasHeightInStage) + padding * 2;
      
      // Use helper method to ensure proper cleanup and creation
      this.createFeltBackground(width, height, padding);
    }
  }

  /**
   * Update Matrix background animation (called from game loop)
   */
  updateMatrixBackground(): void {
    if (this.matrixBackground && this.currentBackgroundType === 'matrix') {
      this.matrixBackground.update();
    }
  }

  private createStockPile(): Pile {
    const pile = new Pile({
      type: "stock",
      x: this.layout.stockX,
      y: this.layout.stockY,
      label: "STOCK",
    });
    pile.name = "Pile-stock";
    return pile;
  }

  private createWastePile(): Pile {
    const pile = new Pile({
      type: "waste",
      x: this.layout.wasteX,
      y: this.layout.wasteY,
      label: "WASTE",
    });
    pile.name = "Pile-waste";
    return pile;
  }

  private createTableauPiles(): void {
    for (let i = 0; i < 7; i++) {
      const pile = new Pile({
        type: "tableau",
        x: this.layout.tableauStartX + i * this.layout.tableauSpacing,
        y: this.layout.tableauStartY,
        cardSpacing: this.layout.cardSpacing,
      });
      pile.name = `Pile-tableau-${i}`;
      this.tableau.push(pile);
    }
  }

  private createFoundationPiles(): void {
    const suits = ["♠", "♥", "♦", "♣"];
    for (let i = 0; i < 4; i++) {
      const pile = new Pile({
        type: "foundation",
        x: this.layout.foundationStartX + i * this.layout.tableauSpacing,
        y: this.layout.foundationStartY,
        label: suits[i],
      });
      pile.name = `Pile-foundation-${suits[i]}`;
      this.foundation.push(pile);
    }
  }

  dealNewGame(): void {
    this.clearAllPiles();
    this.deck.reset();
    
    // Validate deck has 52 cards before shuffling
    if (this.deck.count !== 52) {
      console.error(`Deck validation failed: Expected 52 cards, but deck has ${this.deck.count} cards`);
    }
    
    this.deck.shuffle();

    let cardsDealt = 0;

    // Deal to tableau (28 cards total: 1+2+3+4+5+6+7)
    for (let col = 0; col < 7; col++) {
      for (let row = 0; row <= col; row++) {
        const card = this.deck.deal();
        if (card) {
          card.faceUp = row === col;
          this.tableau[col].addCard(card);
          cardsDealt++;
        } else {
          console.warn(`Missing card at tableau column ${col}, row ${row}`);
        }
      }
    }

    // Remaining cards to stock (should be 24 cards: 52 - 28 = 24)
    let card = this.deck.deal();
    while (card) {
      this.stock.addCard(card);
      cardsDealt++;
      card = this.deck.deal();
    }

    // Validate we have exactly 52 cards
    if (cardsDealt !== 52) {
      console.error(`Card count mismatch: Expected 52 cards, but dealt ${cardsDealt} cards`);
    }

    // Count cards in all piles to verify
    const totalCardsOnBoard = 
      this.tableau.reduce((sum, pile) => sum + pile.cards.length, 0) +
      this.foundation.reduce((sum, pile) => sum + pile.cards.length, 0) +
      this.stock.cards.length +
      this.waste.cards.length;
    
    if (totalCardsOnBoard !== 52) {
      console.error(`Card count on board mismatch: Expected 52 cards, but found ${totalCardsOnBoard} cards`);
    }
  }

  private clearAllPiles(): void {
    const allPiles = [...this.tableau, ...this.foundation, this.stock, this.waste];
    allPiles.forEach(pile => {
      while (!pile.isEmpty()) {
        const card = pile.getTopCard();
        if (card) pile.removeCard(card);
      }
    });
  }

  flipStockToWaste(): boolean {
    if (this.stock.isEmpty()) {
      // Cycle waste back to stock
      this.restockFromWaste();
      return false;
    }

    const card = this.stock.getTopCard();
    if (card) {
      const wasLastCard = this.stock.cards.length === 1;
      this.stock.removeCard(card);
      card.faceUp = true;
      this.waste.addCard(card);
      
      // Only auto-restock if stock became empty AND it wasn't the last card
      // (If it was the last card, let it stay in waste so user can use it)
      if (this.stock.isEmpty() && !this.waste.isEmpty() && !wasLastCard) {
        this.restockFromWaste();
      }
      
      return true;
    }
    return false;
  }

  private restockFromWaste(): void {
    // Cycle waste back to stock
    while (!this.waste.isEmpty()) {
      const card = this.waste.getTopCard();
      if (card) {
        this.waste.removeCard(card);
        card.faceUp = false;
        this.stock.addCard(card);
      }
    }
    
    // Ensure top card is accessible after restocking
    if (!this.stock.isEmpty()) {
      const topCard = this.stock.getTopCard();
      if (topCard) {
        this.stock.removeChild(topCard);
        this.stock.addChild(topCard); // Bring to front
      }
    }
  }

  findPileForCard(card: Card): Pile | null {
    const allPiles = [...this.tableau, ...this.foundation, this.stock, this.waste];
    return allPiles.find(pile => pile.cards.includes(card)) ?? null;
  }

  getAllPiles(): Pile[] {
    return [...this.tableau, ...this.foundation, this.stock, this.waste];
  }

  getFoundationPileBySuit(suit: "♠" | "♥" | "♦" | "♣"): Pile | null {
    const suits = ["♠", "♥", "♦", "♣"];
    const index = suits.indexOf(suit);
    return index >= 0 ? this.foundation[index] : null;
  }

  /**
   * Update layout for responsive design
   * Centers all content within the game board
   */
  updateLayout(screenWidth: number, screenHeight: number): void {
    const newLayout = calculateResponsiveLayout(screenWidth, screenHeight);
    
    // Calculate the bounds of all game content to center it
    const cardWidth = 80;
    const cardHeight = 120;
    
    // Find the rightmost and bottommost positions
    const rightmostX = Math.max(
      newLayout.stockX + cardWidth,
      newLayout.wasteX + cardWidth,
      newLayout.foundationStartX + cardWidth * 4 + newLayout.tableauSpacing * 3,
      newLayout.tableauStartX + cardWidth * 7 + newLayout.tableauSpacing * 6
    );
    
    const bottommostY = newLayout.tableauStartY + cardHeight + (newLayout.cardSpacing * 6);
    
    // Calculate center offset to center all content
    const contentWidth = rightmostX;
    const contentHeight = bottommostY;
    const centerOffsetX = (screenWidth - contentWidth) / 2;
    const centerOffsetY = Math.max(0, (screenHeight - contentHeight) / 2);
    
    // Update stock position with centering offset
    if (this.stock) {
      this.stock.x = newLayout.stockX + centerOffsetX;
      this.stock.y = newLayout.stockY + centerOffsetY;
    }
    
    // Update waste position with centering offset
    if (this.waste) {
      this.waste.x = newLayout.wasteX + centerOffsetX;
      this.waste.y = newLayout.wasteY + centerOffsetY;
    }
    
    // Update foundation positions with centering offset
    this.foundation.forEach((pile, index) => {
      pile.x = newLayout.foundationStartX + index * newLayout.tableauSpacing + centerOffsetX;
      pile.y = newLayout.foundationStartY + centerOffsetY;
    });
    
    // Update tableau positions with centering offset
    this.tableau.forEach((pile, index) => {
      pile.x = newLayout.tableauStartX + index * newLayout.tableauSpacing + centerOffsetX;
      pile.y = newLayout.tableauStartY + centerOffsetY;
    });
    
    // Update card spacing for tableau piles
    this.tableau.forEach(pile => {
      pile.cardSpacing = newLayout.cardSpacing;
      pile.updateAllCardPositions();
    });
    
    // Store new layout with offsets applied
    this.layout = { 
      ...newLayout,
      stockX: newLayout.stockX + centerOffsetX,
      stockY: newLayout.stockY + centerOffsetY,
      wasteX: newLayout.wasteX + centerOffsetX,
      wasteY: newLayout.wasteY + centerOffsetY,
      foundationStartX: newLayout.foundationStartX + centerOffsetX,
      foundationStartY: newLayout.foundationStartY + centerOffsetY,
      tableauStartX: newLayout.tableauStartX + centerOffsetX,
      tableauStartY: newLayout.tableauStartY + centerOffsetY,
    };
  }
}

